/**
 * Module dependencies.
 */

import { AnimationController } from 'src/components/core/animations/animation-controller';
import { Collapse } from 'src/components/core/collapse';
import { FadeInUp } from 'src/components/core/animations/fade-in-up';
import { Grid, SubGrid } from 'src/components/core/layout/grid-content';
import { Heading } from 'src/components/core/heading';
import { Section } from 'src/components/core/layout/section';
import { Svg } from 'src/components/core/svg';
import { Text } from 'src/components/core/text';
import { media } from '@untile/react-core/styles/media';
import { useState } from 'react';
import iconBenefits from 'src/assets/svgs/benefits.svg';
import iconBullet from 'src/assets/svgs/bullet-point.svg';
import iconFaqBrakes from 'src/assets/svgs/faq-brakes.svg';
import iconFaqChassi from 'src/assets/svgs/faq-chassi.svg';
import iconFaqDirection from 'src/assets/svgs/faq-direction.svg';
import iconFaqElectric from 'src/assets/svgs/faq-electric.svg';
import iconFaqEngine from 'src/assets/svgs/faq-engine.svg';
import iconFaqEquipments from 'src/assets/svgs/faq-equipments.svg';
import iconFaqSuspension from 'src/assets/svgs/faq-suspension.svg';
import iconFaqTires from 'src/assets/svgs/faq-tires.svg';
import iconFaqTransmission from 'src/assets/svgs/faq-transmission.svg';
import styled from 'styled-components';

/**
 * `icons` object.
 */

const icons = {
  brakes: iconFaqBrakes,
  chassi: iconFaqChassi,
  direction: iconFaqDirection,
  electric: iconFaqElectric,
  engine: iconFaqEngine,
  equipments: iconFaqEquipments,
  suspension: iconFaqSuspension,
  tires: iconFaqTires,
  transmission: iconFaqTransmission
};

/**
 * `IconKey` type.
 */

type IconKey = keyof typeof icons;

/**
 * `System` type.
 */

export type System = {
  iconKey?: IconKey;
  list: string[];
  title: string;
};

/**
 * `Props` type.
 */

type Props = {
  systems: System[];
  title: string;
};

/**
 * `ListItem` styled component.
 */

const ListItem = styled.li`
  column-gap: 12px;
  display: grid;
  grid-template-columns: 20px 1fr;
  list-style: none;
  padding: 2px 0 2px var(--space-xs);
`;

/*
 * `SystemGrid` component.
 */

const SystemGrid = styled.div`
  display: grid;
  padding-top: var(--space-xs);
  row-gap: var(--space-xxs);

  ${media.min.md`
    padding-top: calc(var(--space-ms) + var(--space-xs));
  `}
`;

/**
 * `System` component.
 */

const System = ({ iconKey, list, title }: System) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Collapse
      icon={iconKey ? icons[iconKey] : null}
      isExpanded={isExpanded}
      onClick={() => setIsExpanded(!isExpanded)}
      title={title}
    >
      <ul>
        {list?.map((item, index) => (
          <ListItem key={index}>
            <Svg icon={iconBullet} size={'20px'} style={{ paddingTop: '1px' }} />

            <Text variant={'bodyL'}>{item}</Text>
          </ListItem>
        ))}
      </ul>
    </Collapse>
  );
};

/*
 * Export `EvaluatedSystems` component.
 */

export const EvaluatedSystems = ({ systems, title }: Props) => (
  <Section style={{ background: 'var(--gradient-tertiary--0-deg)' }}>
    <Grid data-has-offset-gutter>
      <SubGrid style={{ minWidth: 0 }}>
        <AnimationController>
          <Heading
            asTitle={'h3'}
            icon={iconBenefits}
            style={{ padding: '0 var(--space-sm)' }}
            title={title}
            titleSize={28}
          />
        </AnimationController>

        <SystemGrid>
          {systems?.map(({ ...rest }, index) => (
            <FadeInUp key={index} triggerInView>
              <System {...rest} />
            </FadeInUp>
          ))}
        </SystemGrid>
      </SubGrid>
    </Grid>
  </Section>
);
